import { useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { JitsiTrackEvents, JitsiTrackStreamingStatus } from '../base/lib-jitsi-meet';
import { refreshRemoteVideoCounters, remoteMediaTrackId } from '../connection-indicator/remoteVideoStats';
import { canRequestRemoteKeyframe, requestRemoteKeyframe } from '../video-quality/keyframeRequest';
import { REMOTE_VIDEO_STALL_POLL_MS, createRemoteVideoStallMonitor } from './remoteVideoStall';
import { createRemoteVideoStallReporter } from './remoteVideoStallTelemetry';
let lastElementId = 0;
/**
 * Whether the bridge is not delivering the video: the source is not
 * forwarded (inactive) or its data stopped (interrupted).
 *
 * @param {string} [status] - The track's streaming status.
 * @returns {boolean}
 */
export function isPausedStreamingStatus(status) {
    return Boolean(status)
        && (status === JitsiTrackStreamingStatus?.INACTIVE || status === JitsiTrackStreamingStatus?.INTERRUPTED);
}
/**
 * Where available, one requestVideoFrameCallback per poll counts presented
 * frames: a frozen element never answers it.
 *
 * @param {IPaneElement} pane - The pane's element record.
 * @returns {void}
 */
function armFrameCallback(pane) {
    const element = pane.element;
    if (pane.framePending || typeof element.requestVideoFrameCallback !== 'function') {
        return;
    }
    pane.framePending = true;
    element.requestVideoFrameCallback((_now, metadata) => {
        pane.framePending = false;
        if (typeof metadata?.presentedFrames === 'number') {
            pane.presentedFrames = metadata.presentedFrames;
        }
    });
}
/**
 * The element as the stall monitor sees it.
 *
 * @param {IPaneElement} [pane] - The pane's element record.
 * @returns {IVideoElementProbe|undefined}
 */
export function probeVideoElement(pane) {
    const element = pane?.element;
    // A replaced element is detached; its successor reports through elementRef.
    if (!pane || !element?.isConnected) {
        return undefined;
    }
    armFrameCallback(pane);
    const quality = typeof element.getVideoPlaybackQuality === 'function'
        ? element.getVideoPlaybackQuality()
        : undefined;
    return {
        currentTime: element.currentTime,
        elementId: pane.id,
        hasStream: Boolean(element.srcObject),
        paused: element.paused,
        presentedFrames: pane.presentedFrames ?? quality?.totalVideoFrames,
        readyState: element.readyState
    };
}
/**
 * Watches the web stage's remote videos for stalls and recovers them
 * (remoteVideoStall.ts): play() again, then a fresh element, and for a stuck
 * decoder a keyframe through the bridge. Also follows each track's streaming
 * status, so a video the bridge stopped forwarding shows as paused instead of
 * a frozen last frame.
 *
 * @param {string} roomName - The room, for telemetry.
 * @param {Array<IStallGuardPane>} panes - The remote panes.
 * @returns {IRemoteVideoStallGuard}
 */
export function useRemoteVideoStallGuard(roomName, panes) {
    const store = useStore();
    const [view, setView] = useState({});
    const entries = useRef(new Map()).current;
    // Elements are kept apart from the monitors: a pane's element mounts
    // before its video has played, which is when its monitor starts.
    const elements = useRef(new Map()).current;
    const refs = useRef(new Map()).current;
    const room = useRef(roomName);
    const [report] = useState(() => createRemoteVideoStallReporter(() => room.current));
    room.current = roomName;
    const updateView = (paneKey, patch) => {
        setView(current => {
            const was = current[paneKey];
            const next = {
                ...was ?? {
                    attach: 0,
                    stalled: false
                },
                ...patch
            };
            if (was && was.attach === next.attach && was.stalled === next.stalled
                && was.streamingStatus === next.streamingStatus) {
                return current;
            }
            return {
                ...current,
                [paneKey]: next
            };
        });
    };
    const conference = () => store.getState()['features/base/conference']?.conference;
    const sourceName = (entry) => {
        const track = entry.jitsiTrack;
        return typeof track?.getSourceName === 'function' ? track.getSourceName() : undefined;
    };
    const liveElement = (paneKey) => {
        const element = elements.get(paneKey)?.element;
        return element?.isConnected ? element : undefined;
    };
    const act = (paneKey, step) => {
        const entry = entries.get(paneKey);
        if (!entry) {
            return;
        }
        if (step === 'play') {
            const element = liveElement(paneKey);
            Promise.resolve()
                .then(() => element?.play())
                .catch((error) => entry.monitor.noteStepError(error?.name || 'Error'));
        }
        else if (step === 'reattach') {
            // A new key mounts a fresh <video>; Video attaches and plays it.
            setView(current => {
                const was = current[paneKey] ?? {
                    attach: 0,
                    stalled: true
                };
                return {
                    ...current,
                    [paneKey]: {
                        ...was,
                        attach: was.attach + 1
                    }
                };
            });
        }
        else if (!requestRemoteKeyframe(conference(), sourceName(entry) || '')) {
            entry.monitor.noteStepError('keyframe_unavailable');
        }
    };
    const can = (paneKey, step) => {
        const entry = entries.get(paneKey);
        if (!entry) {
            return false;
        }
        if (step === 'keyframe') {
            return Boolean(sourceName(entry)) && canRequestRemoteKeyframe(conference());
        }
        return Boolean(liveElement(paneKey));
    };
    const dispose = (paneKey, entry) => {
        entry.monitor.dispose(Date.now());
        entry.unsubscribe();
        entries.delete(paneKey);
    };
    // Follow the panes on every render: cheap, and a track can change under
    // the same pane.
    useEffect(() => {
        const watched = new Set();
        panes.forEach(pane => {
            const jitsiTrack = pane.videoTrack?.jitsiTrack;
            if (!pane.watch || !jitsiTrack) {
                return;
            }
            watched.add(pane.paneKey);
            const paneKey = pane.paneKey;
            const existing = entries.get(paneKey);
            if (existing?.jitsiTrack === jitsiTrack) {
                return;
            }
            if (existing) {
                dispose(paneKey, existing);
            }
            const onStreamingStatus = (_track, status) => {
                const current = entries.get(paneKey);
                if (current && current.jitsiTrack === jitsiTrack) {
                    current.streamingStatus = status;
                    updateView(paneKey, { streamingStatus: status });
                }
            };
            const streamingStatus = typeof jitsiTrack.getTrackStreamingStatus === 'function'
                ? jitsiTrack.getTrackStreamingStatus()
                : undefined;
            jitsiTrack.on?.(JitsiTrackEvents.TRACK_STREAMING_STATUS_CHANGED, onStreamingStatus);
            entries.set(paneKey, {
                jitsiTrack,
                monitor: createRemoteVideoStallMonitor({
                    act: step => act(paneKey, step),
                    can: step => can(paneKey, step),
                    paneRole: pane.paneRole,
                    report: stall => report(stall, { streamingStatus: entries.get(paneKey)?.streamingStatus })
                }),
                streamingStatus,
                unsubscribe: () => jitsiTrack.off?.(JitsiTrackEvents.TRACK_STREAMING_STATUS_CHANGED, onStreamingStatus)
            });
            updateView(paneKey, {
                stalled: false,
                streamingStatus
            });
        });
        entries.forEach((entry, paneKey) => {
            if (!watched.has(paneKey)) {
                dispose(paneKey, entry);
                updateView(paneKey, {
                    stalled: false,
                    streamingStatus: undefined
                });
            }
        });
    });
    useEffect(() => {
        let polling = false;
        const timer = setInterval(() => {
            if (polling || !entries.size) {
                return;
            }
            polling = true;
            refreshRemoteVideoCounters(conference())
                .then(counters => {
                const at = Date.now();
                const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
                entries.forEach((entry, paneKey) => {
                    const trackId = remoteMediaTrackId(entry.jitsiTrack);
                    const { stalled } = entry.monitor.observe({
                        at,
                        element: probeVideoElement(elements.get(paneKey)),
                        inbound: trackId ? counters?.get(trackId) : undefined,
                        visible
                    });
                    updateView(paneKey, { stalled });
                });
            })
                .finally(() => {
                polling = false;
            });
        }, REMOTE_VIDEO_STALL_POLL_MS);
        return () => {
            clearInterval(timer);
            entries.forEach((entry, paneKey) => dispose(paneKey, entry));
        };
    }, []);
    return {
        attachKey: paneKey => view[paneKey]?.attach ?? 0,
        elementRef: paneKey => {
            let ref = refs.get(paneKey);
            if (!ref) {
                ref = (element) => {
                    // null arrives as a replaced element unmounts, possibly
                    // after its successor mounted; detachment is read at poll
                    // time instead (probeVideoElement).
                    if (!element || elements.get(paneKey)?.element === element) {
                        return;
                    }
                    lastElementId += 1;
                    elements.set(paneKey, {
                        element,
                        framePending: false,
                        id: lastElementId
                    });
                };
                refs.set(paneKey, ref);
            }
            return ref;
        },
        paused: paneKey => {
            const pane = view[paneKey];
            return Boolean(pane?.stalled || isPausedStreamingStatus(pane?.streamingStatus));
        }
    };
}
